import React from "react";
import { shallowEqual } from "react-redux";
import type { FinanceOptionType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import { canNavigateToBuyStep } from "../../../../../../../shared-logic/utils/checkoutUtils";
import type { FinanceRateStateType } from "../../../../../redux/reducers/FinanceRateReducer";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../../redux/store";
import { NavStep } from "../../../../../../shared-logic/utils/Navigation";
import { BnbLabel, useBnbOptionalLabel } from "../../../../../utils/labels";
import { FINANCE_CALCULATOR } from "../../../../../../../../common-deprecated/constants";
import * as Button from "../../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as BasketStyles from "../../../../../../../../common-deprecated/styles/v2/toyota/custom/basket/Basket";
import * as Flex from "../../../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import * as Text from "../../../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import useCtaList from "../../../../../hooks/shared/actions/useCtaList";
import { useFinanceInsuranceNavigation } from "../../../../../hooks/shared/useFinanceInsurance";
import FinanceInsuranceCalculatorLink from "../../../../../utils/FinanceInsuranceCalculatorLink";
import { enabledServicesSelector } from "../../../../../utils/selectorUtils";
import CtaButton from "../../cta/CtaButton";
import * as Styles from "./styles/BasketCtasStyles";
import useBasketCta from "../../../../../hooks/shared/useBasketCta";
import { useDispatch } from "../../../../../../../../shared-logic/redux/store";

type PropsType = {
    step: NavStep;
    financeOption: FinanceOptionType;
    renderFinanceCalculatorAsPrimary: boolean;
    tyCodeSaving: boolean;
    tyCodeSavingFailed: boolean;
};

export const ConsumerBasketCtas: React.FC<PropsType> = (props) => {
    const { step, financeOption } = props;
    const { renderFinanceCalculatorAsPrimary, tyCodeSaving, tyCodeSavingFailed } = props;

    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const finance = useBnbSelector((state) => state.finance);
    const basket = useBnbSelector((state) => state.basket);
    const { showFinance } = useBnbSelector(enabledServicesSelector, shallowEqual);

    const ctas = useCtaList("summaryBasketCta");
    const primaryCta = ctas[0];

    const showCheckoutDisclaimer = !!useBnbOptionalLabel("checkoutDisclaimer");
    const { nextTarget, nextUrl, nextLabel, onNextClick } = useBasketCta({
        step,
        bnbSettings,
        commonSettings,
        dispatch,
    });
    const showNext =
        nextTarget !== NavStep.Buy ||
        canNavigateToBuyStep<FinanceRateStateType>(
            bnbSettings.enableBuy,
            bnbSettings.allowedCheckoutFinanceProduct,
            finance,
            financeOption,
            basket,
        );

    const { onClick: onFinanceClick, financeLabelKey } = useFinanceInsuranceNavigation(step);

    if (step !== NavStep.Summary && showNext && !tyCodeSaving && !tyCodeSavingFailed) {
        return (
            <Button.Primary as="a" href={nextUrl} onClick={onNextClick} width={1} mx={0} mt={3} mb={0}>
                <Text.Button>
                    <BnbLabel
                        label={financeOption === "monthly" ? (`${nextLabel}Monthly` as any) : nextLabel}
                        fallbackLabel={nextLabel}
                    />
                </Text.Button>
            </Button.Primary>
        );
    }
    if (step === NavStep.Summary) {
        return (
            <Flex.Col display="flex" flexDirection="column" alignItems="flex-start" mt={3} px={0}>
                {!tyCodeSaving && !tyCodeSavingFailed && primaryCta && !renderFinanceCalculatorAsPrimary && (
                    <CtaButton width={1} linkType="primary" config={primaryCta}>
                        {primaryCta.title}
                    </CtaButton>
                )}

                {/* This is hidden by CSS to prevent the removal of the eventListener that closes the finance calculator */}
                <Styles.ButtonGroup display={renderFinanceCalculatorAsPrimary ? "block" : "none"}>
                    {bnbSettings?.showFinanceInsuranceInPage || bnbSettings?.enableFinanceCompare ? (
                        showFinance && (
                            <Button.Primary mx={0} width={1} onClick={(event) => onFinanceClick(event, "cc-panel")}>
                                <Text.Button>
                                    <BnbLabel label={financeLabelKey} />
                                </Text.Button>
                            </Button.Primary>
                        )
                    ) : (
                        <FinanceInsuranceCalculatorLink
                            type={FINANCE_CALCULATOR}
                            customButton={({ onClick, label }) => (
                                <Button.Primary mx={0} width={1} onClick={() => onClick()}>
                                    <Text.Button>
                                        <BnbLabel label={label} />
                                    </Text.Button>
                                </Button.Primary>
                            )}
                        />
                    )}
                </Styles.ButtonGroup>
                {showCheckoutDisclaimer && (
                    <>
                        <BasketStyles.DisclaimerWrapper>
                            <BnbLabel
                                label={financeOption === "cash" ? "checkoutDisclaimer" : "checkoutDisclaimerMonthly"}
                                fallbackLabel="checkoutDisclaimer"
                            />
                        </BasketStyles.DisclaimerWrapper>
                    </>
                )}
            </Flex.Col>
        );
    }
    return null;
};
